// Book Inventory view-model. Demand / shortage per 7/30/90-day window stay on
// the server — GET /book-inventory/demand, one row per center × book. Nothing
// here recomputes class demand. It only picks the selected window's fields,
// formats them, derives short/enough from the actual shortage, and shapes an
// order-request prefill from them.

export const BOOK_ORDER_SOURCE = "book-inventory"
export const BOOK_ORDER_REASON = "Book demand for upcoming classes"

export const WINDOW_OPTIONS = [
  { value: "7", label: "Next 7 days" },
  { value: "30", label: "Next 30 days" },
  { value: "90", label: "Next 90 days" },
]

// Filters saved before the 90-day range existed may still hold "all".
export const windowDays = (value) => (value === "7" ? 7 : value === "30" ? 30 : 90)

// Estimated is null while some class has no source-class size yet; the BE
// still sends the part it could compute, shown as a lower bound.
export const formatEstimated = (value, known) => {
  if (value != null) return formatQty(value)
  if (known != null) return `≥ ${formatQty(known)}`
  return "—"
}

// Overview totals per window. The shortage totals were announced as
// actualShortageBooks{N}d; the older actualShortage{N}d is still sent, so
// either name works.
export const overviewAt = (overview, base, days) => {
  const books = overview?.[`${base}Books${days}d`]
  return books !== undefined ? books : overview?.[`${base}${days}d`]
}

export const num = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export const formatQty = (value) => {
  if (value == null || value === "") return "—"
  const n = Number(value)
  if (!Number.isFinite(n)) return "—"
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 })
}

// Per-window field from /demand, e.g. at(row, "actualShortage", 30) → actualShortage30d.
export const at = (row, base, days) => row?.[`${base}${days}d`]

export const rowStock = (row) => row?.currentStock ?? row?.stockRemaining

// Status follows the actual shortage of the selected window; null means the
// BE couldn't compute it (a class isn't mapped to A/B sessions).
export const demandStatus = (row, days) => {
  const shortage = at(row, "actualShortage", days)
  if (shortage == null) return { key: "unknown", tone: "neutral", label: "Not calculated" }
  if (Number(shortage) > 0) return { key: "short", tone: "danger", label: "Short" }
  return { key: "enough", tone: "success", label: "Enough" }
}

const isShortStatus = (row, days) => demandStatus(row, days).key === "short"

export const isUnmappedRow = (row) =>
  row?.mappingMissing === true || row?.hasMapping === false || row?.mapped === false || (row?.itemId == null && !row?.itemName)

export const demandRowKey = (row) => {
  if (row?.itemId != null) return `${row.centerId ?? ""}:${row.itemId}`
  return `${row?.centerId ?? ""}:unmapped:${row?.classId ?? row?.courseName ?? ""}:${row?.level ?? row?.levelName ?? ""}`
}

// Books to order = the window's actual shortage, rounded up so a fractional
// book still gets ordered. 0 when covered or not calculated.
export const shortageQuantity = (row, days) => {
  const shortage = num(at(row, "actualShortage", days))
  return shortage > 0 ? Math.ceil(shortage - 1e-9) : 0
}

const isZero = (value) => value != null && num(value) === 0

const DEMAND_TOTAL_KEYS =["actualNeeded", "estimatedNeeded", "stock", "actualShortage", "estimatedShortage"]

const totalValue = (row, key, days) => (key === "stock" ? rowStock(row) : at(row, key, days))

// Group by center (book rows inside) or by book (center rows inside). Totals
// sum the service's per-row numbers; a total with a null row is marked
// incomplete and shown as a lower bound, like the Number Blocks.
export const groupDemandRows = (rows = [], by = "center", days = 30) => {
  const groups = new Map()
  rows.forEach((row) => {
    const key = by === "item" ? `item:${row.itemId ?? demandRowKey(row)}` : `center:${row.centerId ?? ""}`
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: by === "item" ? row.itemName || row.itemCode || "No book mapping configured" : row.centerName || `Center ${row.centerId}`,
        imageUrl: by === "item" ? row.imageUrl || null : null,
        rows: [],
        totals: Object.fromEntries(DEMAND_TOTAL_KEYS.map((k) => [k, 0])),
        incomplete: new Set(),
        shortCount: 0,
        orCount: 0,
      })
    }
    const group = groups.get(key)
    group.rows.push(row)
    DEMAND_TOTAL_KEYS.forEach((k) => {
      const value = totalValue(row, k, days)
      if (value == null) group.incomplete.add(k)
      else group.totals[k] += num(value)
    })
    if (isShortStatus(row, days)) group.shortCount += 1
    if (row.orderStatus === "HAS_OR") group.orCount += 1
  })
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label))
}

// shortage: "actual" | "estimated" keeps rows short of that type in the
// window; orStatus: "HAS_OR" | "NO_OR".
export const filterDemandRows = (rows = [], { search = "", window = "30", shortage = "", orStatus = "" } = {}) => {
  const days = windowDays(window)
  const query = search.trim().toLowerCase()
  return rows.filter((row) => {
    if (query) {
      const hay = [row.centerName, row.itemName, row.itemCode, row.courseName, row.level, row.levelName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      if (!hay.includes(query)) return false
    }
    // No demand at all in the window (e.g. only 31–90 days out at 30). Null
    // means not calculated, so the row stays.
    if (isZero(at(row, "actualNeeded", days)) && isZero(at(row, "estimatedNeeded", days))) return false
    if (shortage && !(num(at(row, `${shortage}Shortage`, days)) > 0)) return false
    if (orStatus && row.orderStatus !== orStatus) return false
    return true
  })
}

// DD/MM/YYYY from the calendar date in the string. `new Date("2026-10-21")`
// is UTC midnight and can render as the previous local day.
export const formatStartDate = (value) => {
  if (!value) return "—"
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return "—"
  return `${match[3]}/${match[2]}/${match[1]}`
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// "21/10/2026 (Wed)" — weekday from the calendar date, not a UTC parse.
export const formatStartDay = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return "—"
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getDay()
  return `${formatStartDate(value)} (${WEEKDAYS[day]})`
}

// Label comes from the API (`Today`, `In N days`); only the colour is ours.
export const classTone = (daysUntil) => {
  const days = daysUntil == null || daysUntil === "" ? NaN : Number(daysUntil)
  if (!Number.isFinite(days)) return "neutral"
  if (days < 7) return "danger"
  if (days <= 14) return "warning"
  return "info"
}

const lineNote = (row, shortage, days) => {
  const bits = [
    "Source: Book Inventory.",
    `Actual demand +30% (${days} days): ${formatQty(at(row, "actualNeeded", days))}.`,
    `Estimated demand +30% (${days} days): ${formatQty(at(row, "estimatedNeeded", days))}.`,
    `Stock: ${formatQty(rowStock(row))}.`,
    `Actual shortage (${days} days): ${formatQty(at(row, "actualShortage", days))}.`,
  ]
  if (shortage > 0) bits.push(`Order quantity is the shortage (${formatQty(shortage)}).`)
  else bits.push("No actual shortage. Adjust the quantity if you still want to order.")
  return bits.join(" ")
}

// `overrides` comes from the single-row confirm modal (quantity / reason).
export const buildOrderPrefill = (rows = [], itemsById = new Map(), overrides = {}, days = 30) => {
  const lines = rows.map((row) => {
    const item = row?.itemId != null ? itemsById.get(String(row.itemId)) : null
    const shortage = shortageQuantity(row, days)
    const price = Number(item?.standardPrice ?? row.standardPrice)
    return {
      centerId: row.centerId ?? "",
      itemId: row.itemId ?? null,
      itemName: row.itemName || item?.name || "",
      itemCode: row.itemCode || item?.itemCode || "",
      uom: row.uom || item?.uom || "",
      groupItemId: item?.itemGroupId || item?.groupId || row.itemGroupId || row.groupId || "",
      categoryId: item?.categoryId ?? row.categoryId ?? null,
      unitPrice: Number.isFinite(price) && price >= 0 ? price : 0,
      quantity: overrides.quantity || (shortage > 0 ? shortage : 1),
      note: lineNote(row, shortage, days),
    }
  })
  const categories = [...new Set(lines.map((line) => line.categoryId).filter((id) => id != null && id !== ""))]
  const regions = [...new Set(rows.map((row) => row.region).filter((region) => region === "North" || region === "South"))]
  const centers = [...new Set(rows.map((row) => row.centerName).filter(Boolean))]
  return {
    source: BOOK_ORDER_SOURCE,
    title: centers.length === 1 ? `Book demand — ${centers[0]}` : BOOK_ORDER_REASON,
    reason: overrides.reason || BOOK_ORDER_REASON,
    region: regions.length === 1 ? regions[0] : "",
    category: categories.length === 1 ? categories[0] : "",
    lines,
  }
}
